import { Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useState, DragEvent, useEffect, useRef } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import {
  CustomFieldType,
  ICustomFieldAnswer,
  ICustomFieldValidation,
} from "../../../../@types/CustomField";
import { Copy } from "../../../../assets/icons/Copy";
import { DragHandle } from "../../../../assets/icons/DragHandle";
import { Minus } from "../../../../assets/icons/Minus";
import { Plus } from "../../../../assets/icons/Plus";
import { DateTimePickerInput } from "../../../../components/DateTimePicker";
import { IOption, SelectDropdown } from "../../../../components/SelectDropdown";
import { SnomedSelect } from "../../../../components/SnomedSelect";
import { TextField } from "../../../../components/TextField";
import { AlertText } from "../../../../components/TextField/TextField.styles";
import { ToastNotification } from "../../../../components/ToastNotification";
import { searchAllergyOptions } from "../../../../hooks/queries/allergies";
import { searchDiagnosisOptions } from "../../../../hooks/queries/diagnoses";
import { useWardLabel } from "../../../../hooks/useWardLabel";
import { css } from "../../../../styles";
import { mixpanelActions } from "../../../../utils/mixpanel";
import { getPortalDateFormat } from "../../../../utils/portalDateFormat";
import { cardsCofigs } from "../PatientLateralMenu/cardsConfigs";
import { CustomFieldCardEditor } from "./CustomFieldCardEditor";
import {
  GrabButton,
  multiSelectCss,
  PatientMenuCardContainer,
  PatientMenuCardContent,
  PatientMenuCardControls,
  selectCss,
  textFieldCss,
  VisibilityButton,
  CopyContainer,
} from "./PatientMenuCard.styles";

// EN-5067: metadata carried on a card that represents a dynamic per-ward custom field. Its
// `name` is `custom:<definitionId>` (stable identity); the human label/type drive the read-only
// display, and options/validation/isRequired drive the inline editor + client validation.
// The DOB card's description, as getShortFormattedUtcDate writes it ("Nov 1, 1968").
const CARD_DATE_FORMAT = "MMM D, YYYY";

export type CustomFieldCardMeta = {
  definitionId: string;
  label: string;
  type: CustomFieldType;
  options: string[];
  validation: ICustomFieldValidation;
  isRequired: boolean;
};

export type PatientCard = {
  /**
   * The card's stable identity. It drives the per-field validation below, the input's DOM
   * name, the save payload, the onboarding-code lookup and the persisted card order — so it
   * stays in English. `labelKey` is what the user reads (EN-5191).
   */
  name: string;
  /** Translation key for the card title. Falls back to `name` when absent. */
  labelKey?: string;
  description?: string;
  order: number;
  active: boolean;
  customFieldMeta?: CustomFieldCardMeta;
};

type PatientMenuCardProps = {
  card: PatientCard;
  isEditing: boolean;
  isEditable: boolean;
  isRequired?: boolean;
  dropdownItems?: IOption[];
  onDragOver: (event: DragEvent<HTMLDivElement>) => void;
  onCardButtonClick: () => void;
  onPropEdit: (
    propName: string,
    newValue: string | null,
    oldValue: string
  ) => void;
  onError?: (error: boolean) => void;
  // EN-4960: the Ward card is a multi-select whose selection lives in the parent (it saves via
  // PUT /Wards, not the JSON-patch flow). When these are provided the card renders that editor.
  isWardCard?: boolean;
  wardOptions?: IOption[];
  wardValue?: string[];
  onWardChange?: (values: string[]) => void;
  // EN-5067: a custom-field card is edited inline (like Ward) and saved via PUT /CustomFields.
  // When editing, the parent seeds `customFieldValue` and receives changes via onCustomFieldChange.
  customFieldValue?: ICustomFieldAnswer;
  onCustomFieldChange?: (
    definitionId: string,
    answer: ICustomFieldAnswer
  ) => void;
};

/**
 * `error` holds a translation **key**, not a sentence, and is resolved at render (EN-5191).
 * Keeping the key in state is what lets the required-field checks below compare against this
 * constant — a comparison on the rendered sentence would stop matching the moment the panel is
 * shown in another language.
 */
const REQUIRED_ERROR_MESSAGE = "patientPage_cards_errors_required";

// Parses a SNOMED card description ("<term> - <conceptId>, ...") back into the
// option shape SnomedSelect works with. Used for both the trigger/chips display
// and the required-field check, so the card no longer needs the full concept list.
function parseSnomedDisplay(display?: string): IOption[] {
  if (!display || display === "-") return [];

  return display.split(", ").map((label) => {
    const separatorIndex = label.lastIndexOf(" - ");
    const conceptId =
      separatorIndex === -1 ? label : label.slice(separatorIndex + 3);

    return { label, value: conceptId, key: `snomed-${conceptId}` };
  });
}

export function PatientMenuCard({
  card,
  isEditing,
  isEditable,
  isRequired = false,
  dropdownItems,
  onDragOver,
  onCardButtonClick,
  onPropEdit,
  onError,
  isWardCard = false,
  wardOptions,
  wardValue,
  onWardChange,
  customFieldValue,
  onCustomFieldChange,
}: PatientMenuCardProps) {
  const { t } = useTranslation();

  const wardLabel = useWardLabel();
  const isActive = card.active;

  // "Ward" is the stable identity key used throughout the card logic (case matches,
  // prop names, dropdown wiring). Only the rendered title is swapped for the hospital's
  // configured label — the key itself must never change or that logic breaks.
  // EN-5067: custom-field cards keep a `custom:<id>` identity key but render their human label.
  // No " *" suffix here: required is an Add Patient concern, and on the patient details the card
  // title stays a plain label (the required check still guards the inline edit / Save).
  const displayName = (() => {
    if (card.customFieldMeta) {
      return card.customFieldMeta.label;
    }
    if (card.name === "Ward") return wardLabel.singular;
    return card.labelKey ? t(card.labelKey) : card.name;
  })();

  const cardRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);

  // EN-5067: custom Multiselect values are shown as chips (one per line), reusing the same
  // read-only newline rendering as the SNOMED Allergies/Diagnosis cards.
  const isMultiSelect =
    card.name === "Allergies" ||
    card.name === "Diagnosis" ||
    card.customFieldMeta?.type === "Multiselect";

  const [isOverGrab, setIsOverGrab] = useState(false);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [isEditingCard, setIsEditingCard] = useState(false);
  const [dropdownValue, setDropdownValue] = useState<string | undefined>();
  // EN-4960: the Ward multi-select is self-controlled — seeded once from `wardValue` on mount
  // (the card remounts when the Edit session starts) and reported up via onWardChange. Keeping
  // the selection here (not driven by a parent value on every change) stops the card list from
  // remounting mid-selection and closing the dropdown.
  const [wardSelection, setWardSelection] = useState<string[]>(wardValue ?? []);
  const [showNotification, setShowNotification] = useState(false);

  const [value, setValue] = useState(card.description);
  const [error, setError] = useState<string>("");

  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  const ppsnRegex = /^\d{7}[A-Za-z]{1,2}$/;
  const ihiRegex = /^\d{10,18}$/;

  function setDraggingOfCardRef(active: boolean) {
    cardRef.current?.setAttribute("dragging", String(active));
  }

  useEffect(() => {
    setDraggingOfCardRef(false);
    cardRef.current?.setAttribute("order", card.order.toString());
    cardRef.current?.setAttribute("name", card.name);
  }, []);

  useEffect(() => {
    if (onError) {
      onError(error !== "");
    }
  }, [error]);

  useEffect(() => {
    if (card.name === "Weight (kg)" || card.name === "Height (cm)") {
      if (parseInt(value ?? "", 10) > 0) {
        setValue(value);
      }
      return;
    }

    if (card.name === "Email") {
      const email = value;
      if (email === "") {
        setError("patientPage_cards_errors_emailRequired");
      } else if (!emailRegex.test(email ?? "")) {
        setError("patientPage_cards_errors_emailInvalid");
      } else {
        setError("");
      }
    }

    if (card.name === "Next of kin email") {
      const email = value?.trim() ?? "";
      if (email === "" || email === "-" || emailRegex.test(email)) {
        setError("");
      } else {
        setError("patientPage_cards_errors_emailInvalid");
      }
    }

    if (card.name === "PPSN") {
      const ppsn = value?.trim() ?? "";
      if (ppsn === "" || ppsn === "-" || ppsnRegex.test(ppsn)) {
        setError("");
      } else {
        setError("patientPage_cards_errors_ppsn");
      }
    }

    if (card.name === "IHI") {
      const ihi = value?.trim() ?? "";
      if (ihi === "" || ihi === "-" || ihiRegex.test(ihi)) {
        setError("");
      } else {
        setError("patientPage_cards_errors_ihi");
      }
    }
  }, [value]);

  // Enforce the hospital's per-field required flag: empty values on a required field
  // surface a blocking error which propagates via onError to disable the lateral menu's
  // Save button. "-" is treated as empty because that's the placeholder shown for
  // missing values in the card description. Placed AFTER the field-specific format
  // useEffect so it wins on empty values — the format effects clear the error when
  // the field is empty (because "empty" passes their regex checks), so the required
  // check has to run last to get the final word.
  useEffect(() => {
    // The required-field check only blocks the save while the card is active. Inactive
    // cards aren't shown on the patient details, so an empty required field that the user
    // hasn't enabled must not disable the Save button. Toggling a card inactive also
    // clears any pending required-error so it stops blocking the save.
    if (!isRequired || !isActive) {
      if (error === REQUIRED_ERROR_MESSAGE) {
        setError("");
      }
      return;
    }

    const isEmpty = isMultiSelect
      ? parseSnomedDisplay(value).length === 0
      : !value || value.trim() === "" || value === "-";

    if (isEmpty) {
      setError(REQUIRED_ERROR_MESSAGE);
    } else if (error === REQUIRED_ERROR_MESSAGE) {
      setError("");
    }
  }, [isRequired, isActive, value, isMultiSelect]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        dropdownItems?.length === 0 &&
        card.name !== "DOB" &&
        // EN-5067: custom-field cards also get an empty dropdownItems list, but they are NOT
        // edited via the JSON-patch text input below (their input is named `custom-field-<id>`,
        // not `patient-prop-input-<name>`). Excluding them here sends them to the setTimeout
        // close branch instead of a `.value` read on a non-existent element (which would throw).
        !card.customFieldMeta
      ) {
        const textFieldComponent = document.getElementsByName(
          `patient-prop-input-${card.name}`
        );

        const inputValue = (textFieldComponent[0] as HTMLInputElement).value;

        if (inputValue !== card.description) {
          onPropEdit(card.name, inputValue, card.description ?? "");
        } else {
          onPropEdit(card.name, null, card.description);
        }

        setIsEditingCard(false);
      }
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        const classesToCheck = [
          "ant-select-item-option-content",
          "ant-select-item-option",
          "ant-select-selection-overflow",
        ];

        // EN-5272: the date picker's calendar is portalled to <body>, outside inputRef. A click on
        // it (a day, the month arrows) is part of editing the card, not a click away from it.
        if ((event.target as HTMLElement).closest?.(".ant-picker-dropdown")) {
          return;
        }

        if (
          !classesToCheck.some((className) =>
            (event.target as HTMLElement).classList.contains(className)
          )
        ) {
          setTimeout(() => {
            setIsEditingCard(false);
          }, 100);
        }
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [inputRef]);

  useEffect(() => {
    // SNOMED cards resolve their selection from the description directly (via
    // parseSnomedDisplay), so there is nothing to seed from the dropdown list.
    if (isMultiSelect) {
      return;
    }

    const currentValue = dropdownItems?.find(
      (x) => x.label === card.description
    );

    if (dropdownValue !== currentValue?.value.toString()) {
      setDropdownValue(currentValue?.value.toString());
    }
  }, [dropdownItems, card.description, isMultiSelect]);

  // EN-4960: seed the ward multi-select from the parent's `wardValue` whenever its contents
  // change (i.e. when an Edit session starts). Keyed on the joined value rather than the array
  // reference so a fresh reference on every parent re-render doesn't wipe the user's in-progress
  // picks mid-edit. Needed because the card no longer remounts when Edit begins (stable key).
  const wardValueKey = (wardValue ?? []).join(",");
  useEffect(() => {
    if (isWardCard) {
      setWardSelection(wardValue ?? []);
    }
  }, [isWardCard, wardValueKey]);

  // Inactive cards are hidden unless we're in an edit session (where they can be
  // re-enabled). This must sit AFTER every hook: with the stable `key={card.name}`
  // the card fiber persists across the Edit toggle, so returning early above the
  // hooks would change the hook count between renders and violate the Rules of
  // Hooks ("Rendered more hooks than during the previous render").
  if (!isEditing && !isActive) return null;

  const handleDropdownValue = (selectedValue: string | string[]) => {
    if (typeof selectedValue !== "string") return;

    const newValue = dropdownItems?.find(
      (x) => x.value.toString() === selectedValue
    );

    if (newValue && newValue?.label !== card.description) {
      onPropEdit(card.name, newValue?.label, card.description ?? "");
      setDropdownValue(newValue?.value.toString());
      setValue(newValue?.label);
    } else {
      onPropEdit(card.name, null, card.description ?? "");
    }

    setIsEditingCard(false);
  };

  const handleSnomedChange = (selected: IOption[]) => {
    const nextDisplay =
      selected.length > 0
        ? selected.map((option) => option.label).join(", ")
        : "-";

    setValue(nextDisplay);

    if (nextDisplay !== (card.description ?? "-")) {
      onPropEdit(card.name, nextDisplay, card.description ?? "-");
    } else {
      onPropEdit(card.name, null, card.description ?? "");
    }
  };

  const handleOnDragStart = () => {
    setIsEditingCard(false);

    const isValidDragging = isEditing && isOverGrab;

    if (!isValidDragging) return;

    setDraggingOfCardRef(true);
  };

  const handleOnDragEnd = () => {
    if (isEditing) setDraggingOfCardRef(false);
  };

  const handleOnDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (isEditing) onDragOver(event);
  };

  const handleTextFieldKeyDown = (event: any) => {
    if (event.key === "Enter" && isEditingCard) {
      if (value !== card.description) {
        onPropEdit(card.name, value ?? "", card.description ?? "");
      }

      setIsEditingCard(false);
    }
  };

  const handleInputKeyDown = (event: any) => {
    if (event.key === "Enter" && !isEditingCard && isEditing) {
      setIsEditingCard(true);
    }
    if (event.keyCode === 27 && isEditingCard && isEditing) {
      setIsEditingCard(false);
    }
  };

  const handleShowTooltip = () => {
    if (isEditing) {
      if (!isEditable && card.active) {
        return true;
      }
    }
    return false;
  };

  // EN-5272: the DOB is edited with the portal's date picker. It only commits a real date, so the
  // old format check is gone; what reaches onPropEdit is still the card's "MMM D, YYYY" description,
  // which PatientLateralMenu turns into the YYYY/MM/DD it sends.
  const handleOnDateChange = (date: Dayjs | null) => {
    if (!date?.isValid()) {
      // Cleared: a date of birth cannot be removed from here, so keep the editor open and say so.
      setError("patientPage_cards_errors_date");
      return;
    }

    const formattedDate = date.format(CARD_DATE_FORMAT);

    setValue(formattedDate);
    onPropEdit(card.name, formattedDate, card.description ?? "");

    setError("");
    setIsEditingCard(false);
  };

  // Stops the input at the width of the column the value lands in, so an over-long value is
  // caught while typing rather than by a toast that closes the panel and discards every edit.
  const maxLength = cardsCofigs.find(
    (config) => config.name === card.name
  )?.maxLength;

  const handleInputType = () => {
    if (card.name === "DOB" && value) {
      const dateOfBirth = dayjs(value, CARD_DATE_FORMAT, true);

      return (
        <>
          <DateTimePickerInput
            showTime={false}
            format={getPortalDateFormat()}
            placeholder={getPortalDateFormat()}
            width="143px"
            height="28px"
            maxDate={dayjs()}
            value={dateOfBirth.isValid() ? dateOfBirth : null}
            // Clearing hands back null despite the signature.
            onChange={handleOnDateChange}
          />
          {error && <AlertText type="error">{t(error)}</AlertText>}
        </>
      );
    }

    if (dropdownItems?.length === 0) {
      return (
        <TextField
          label=""
          className={textFieldCss()}
          value={value ?? "-"}
          onChange={(value) => setValue(value.target.value)}
          onKeyDown={handleTextFieldKeyDown}
          name={`patient-prop-input-${card.name}`}
          type={
            card.name === "Weight (kg)" || card.name === "Height (cm)"
              ? "number"
              : undefined
          }
          maxLength={maxLength}
          errorMessage={error ? t(error) : ""}
        />
      );
    }

    return (
      <SelectDropdown
        key={uuidv4()}
        options={dropdownItems ?? []}
        value={dropdownValue}
        width={135}
        onValueChange={handleDropdownValue}
        className={selectCss()}

        // Multiple conditions code
        // multiple={card.name === "Condition"}
        // value={
        //   card.name !== "Condition" ? dropdownValue : multipleDropdownValue
        // }
        // className={card.name !== "Condition" ? selectCss() : multiSelectCss()}
      />
    );
  };

  const activeOverGrab = () => setIsOverGrab(true);

  const inactiveOverGrab = () => setIsOverGrab(false);

  const inputHoverCss = css({
    "&:hover": {
      backgroundColor: "var(--colors-patient-details-edit-hover)",
    },
  });

  const onCopyText = () => {
    mixpanelActions.track(`UserAction: Copy${card.name.replace(" ", "")}`);

    setShowNotification(true);
    // Reset state back after 2.5 seconds when toast closes
    setInterval(() => {
      setShowNotification(false);
    }, 2500);
  };

  const getTooltipButton = () => {
    return (
      <Tooltip
        title={
          handleShowTooltip() ? t("patientPage_cards_notEditable") : undefined
        }
        zIndex={3002}
        placement="top"
        styles={{
          body: {
            height: "100%",
            width: "fit-content",
            fontFamily: "'Open Sans', sans-serif",
          },
        }}
      >
        <button
          onClick={() =>
            isEditing && isEditable ? setIsEditingCard(true) : null
          }
          onKeyDown={handleInputKeyDown}
          type="button"
          style={{
            maxWidth: isEditing ? "160px" : "200px",
            overflow: "hidden",
            // break-word wraps only between words (and only breaks a single
            // word when it can't fit), so allergies/diagnoses keep one item
            // per line without splitting a word mid-character ("h" / "and").
            wordBreak: "break-word",
            // SNOMED cards list one allergy/diagnosis per line instead of a
            // single comma-separated run.
            whiteSpace: isMultiSelect ? "pre-line" : undefined,
            textAlign: "left",
            cursor: handleShowTooltip() ? "not-allowed" : "pointer",
          }}
          className={
            !handleShowTooltip() && isEditing && isActive ? inputHoverCss() : ""
          }
        >
          {isMultiSelect && value && value !== "-"
            ? value.split(", ").join("\n")
            : value}
        </button>
        {!isEditing && isMouseOver && (
          <CopyContainer>
            <Copy />
          </CopyContainer>
        )}
      </Tooltip>
    );
  };

  const getTooltipButtonCopy = () => {
    if (isEditing) {
      return getTooltipButton();
    }
    return (
      <CopyToClipboard text={value || "-"} onCopy={onCopyText}>
        {getTooltipButton()}
      </CopyToClipboard>
    );
  };

  const renderCardValue = () => {
    // EN-5067: an editable custom-field card follows the same text→click→edit pattern as the
    // other cards — it shows as plain read-only text until the user clicks it (which sets
    // isEditingCard), then reveals the type-appropriate editor. Both states are handled here and
    // return early so a Multiselect custom field never falls through into the SNOMED block below.
    // Changes are reported to the parent, which saves them via PUT /CustomFields on the global Save.
    if (
      card.customFieldMeta &&
      isEditing &&
      isEditable &&
      isActive &&
      onCustomFieldChange
    ) {
      if (!isEditingCard) {
        return getTooltipButtonCopy();
      }

      return (
        <div ref={inputRef} style={{ width: "143px" }}>
          <CustomFieldCardEditor
            field={card.customFieldMeta}
            value={customFieldValue ?? {}}
            onChange={(answer) =>
              onCustomFieldChange(card.customFieldMeta!.definitionId, answer)
            }
            onError={(hasError) => onError?.(hasError)}
          />
        </div>
      );
    }

    // Ward follows the same text→click→edit pattern as the other cards: it shows as
    // plain text until the user clicks it (which sets isEditingCard), then reveals the
    // multi-select. Wrapping it in inputRef lets the shared click-outside handler close
    // it, exactly like the single-select dropdown cards.
    if (isWardCard && isEditing && isEditable && isActive && isEditingCard) {
      return (
        <div ref={inputRef} style={{ width: "143px" }}>
          <SelectDropdown
            multiple
            options={wardOptions ?? []}
            value={wardSelection}
            width={135}
            className={multiSelectCss()}
            placeholder={t("addPatient_placeholders_chooseWards", {
              ward: wardLabel.pluralLower,
            })}
            onValueChange={(selected) => {
              const next = Array.isArray(selected) ? selected : [selected];
              setWardSelection(next);
              onWardChange?.(next);

              // Keep the collapsed text in sync so, once closed, the card shows the
              // selected ward names instead of the stale description.
              const labels = (wardOptions ?? [])
                .filter((option) => next.includes(option.value))
                .map((option) => option.label);
              setValue(labels.length ? labels.join(", ") : "-");
            }}
          />
        </div>
      );
    }

    if (isMultiSelect && isEditing && isEditable && isActive) {
      return (
        <SnomedSelect
          value={parseSnomedDisplay(value)}
          onChange={handleSnomedChange}
          searchOptions={
            card.name === "Diagnosis"
              ? searchDiagnosisOptions
              : searchAllergyOptions
          }
          queryKey={card.name === "Diagnosis" ? "diagnoses" : "allergies"}
          triggerVariant="card"
          modalTitle={t(
            card.name === "Diagnosis"
              ? "patientPage_cards_snomed_addDiagnosis"
              : "patientPage_cards_snomed_addAllergies"
          )}
          placeholder={t(
            card.name === "Diagnosis"
              ? "patientPage_cards_snomed_selectDiagnosis"
              : "patientPage_cards_snomed_selectAllergies"
          )}
          searchPlaceholder={t(
            card.name === "Diagnosis"
              ? "patientPage_cards_snomed_searchDiagnosis"
              : "patientPage_cards_snomed_searchAllergies"
          )}
        />
      );
    }

    if (!isEditingCard) {
      return getTooltipButtonCopy();
    }

    return (
      <div
        ref={inputRef}
        style={{
          width: "143px",
        }}
      >
        {handleInputType()}
      </div>
    );
  };

  return (
    <PatientMenuCardContainer
      ref={cardRef}
      isVisible={isActive}
      onDragEnd={handleOnDragEnd}
      onDragStart={handleOnDragStart}
      onDragOver={handleOnDragOver}
      draggable={isOverGrab && isActive && isEditing}
      onMouseOver={() => setIsMouseOver(true)}
      onMouseLeave={() => setIsMouseOver(false)}
    >
      <PatientMenuCardContent>
        <p>
          {displayName}
          {isRequired ? " *" : ""}
        </p>
        {renderCardValue()}
        {showNotification ? (
          <ToastNotification
            message={t("patientPage_cards_copied")}
            type="success"
            width="230px"
            rightMargin="220px"
          />
        ) : null}
        {/* The TextField renders its own errorMessage inline while editing.
            When the user closes the editor (Enter / blur / clicks away) the
            input disappears and the error would vanish with it - so we also
            render the error here when out of edit mode, so required-empty
            and format errors stay visible without re-opening the editor. */}
        {!isEditingCard && error && (
          <AlertText type="error">{t(error)}</AlertText>
        )}
      </PatientMenuCardContent>

      {isEditing && (
        <PatientMenuCardControls>
          {isActive && isMouseOver && (
            <GrabButton
              onMouseEnter={activeOverGrab}
              onMouseLeave={inactiveOverGrab}
            >
              <DragHandle />
            </GrabButton>
          )}

          <VisibilityButton isActive={isActive} onClick={onCardButtonClick}>
            {isActive ? <Minus /> : <Plus />}
          </VisibilityButton>
        </PatientMenuCardControls>
      )}
    </PatientMenuCardContainer>
  );
}
